import { isValid, parseISO } from "date-fns";
import { formatInTimeZone } from "date-fns-tz";
import {
    fullDateFullYearFormat,
    timeFormat,
    toWorkspaceDate,
} from "@common/helpers/date";
import { isObject } from "@common/helpers/typeGuards";
import type { WorkspaceTimezone } from "@common/workspaceTimezone";
import type { DateOnlyRange } from "@design-system/DatePicker/DatePicker.types";
import { formatDateOnly } from "@helpers/date";
import type {
    DataGridPeopleMember,
    DataGridPersonValue,
    DataGridWorkspaceValue,
} from "./cellValueTypes";
import { asDateOnly } from "./dateOnlyValue";

/** Who a person column shows for `null` with `fallback: "kick"`: the Kick bot. */
export const KICK_PERSON: DataGridPersonValue = { name: "Kick" };

/** A people avatar's label: "Ana Ruiz (Preparer, Reviewer)", or just the name. */
export const toPeopleLabel = (member: DataGridPeopleMember): string =>
    member.roles === undefined || member.roles.length === 0
        ? member.name
        : `${member.name} (${member.roles.join(", ")})`;

/** The person a cell shows: `null` reads as Kick with `fallback: "kick"`. */
export const toPersonDisplay = (
    value: DataGridPersonValue | null,
    fallback: "kick" | undefined,
): DataGridPersonValue | null =>
    value === null && fallback === "kick" ? KICK_PERSON : value;

/** A valid instant from an ISO string or `Date`, else null. */
export const toInstant = <TValue>(value: TValue): Date | null => {
    const instant = typeof value === "string" ? parseISO(value) : value;
    return instant instanceof Date && isValid(instant) ? instant : null;
};

/**
 * The workspace day an instant falls on (`toWorkspaceDate`) and its time
 * there ("1:30 AM"), or null for an invalid instant.
 */
export const getTimestampParts = ({
    value,
    timeZone,
}: {
    value: string | Date;
    timeZone: WorkspaceTimezone;
}): { dateOnly: string; time: string } | null => {
    const instant = toInstant(value);
    if (instant === null) {
        return null;
    }
    return {
        dateOnly: toWorkspaceDate({ date: instant, timezone: timeZone }),
        time: formatInTimeZone(instant, timeZone, timeFormat),
    };
};

const yearOf = (dateOnly: string) => Number(dateOnly.slice(0, 4));

const formatDay = ({
    dateOnly,
    isYearShown,
}: {
    dateOnly: string;
    isYearShown: boolean;
}) => formatDateOnly(dateOnly, isYearShown ? fullDateFullYearFormat : "MMM d");

/**
 * "Jan 1 – Mar 31": both years when they differ, none in `currentYear`,
 * else the year once at the end. An open side reads "From Jan 1" or
 * "Until Mar 31"; no days at all is null.
 */
export const formatDateOnlyRange = ({
    range,
    currentYear,
}: {
    range: DateOnlyRange;
    currentYear: number;
}): string | null => {
    const from = asDateOnly(range.from);
    const to = asDateOnly(range.to);
    const isYearShown = (dateOnly: string) => yearOf(dateOnly) !== currentYear;
    if (from === null) {
        return to === null
            ? null
            : `Until ${formatDay({ dateOnly: to, isYearShown: isYearShown(to) })}`;
    }
    if (to === null) {
        return `From ${formatDay({ dateOnly: from, isYearShown: isYearShown(from) })}`;
    }
    const isSameYear = yearOf(from) === yearOf(to);
    const fromText = formatDay({ dateOnly: from, isYearShown: !isSameYear });
    const toText = formatDay({
        dateOnly: to,
        isYearShown: isYearShown(to) || !isSameYear,
    });
    return `${fromText} – ${toText}`;
};

export const isPersonValue = <TValue>(
    value: TValue,
): value is TValue & DataGridPersonValue =>
    isObject(value) && "name" in value && typeof value.name === "string";

export const isPeopleValue = <TValue>(
    value: TValue,
): value is TValue & readonly DataGridPeopleMember[] =>
    Array.isArray(value) && value.every(isPersonValue);

export const isWorkspaceValue = <TValue>(
    value: TValue,
): value is TValue & DataGridWorkspaceValue =>
    isObject(value) &&
    "name" in value &&
    typeof value.name === "string" &&
    "iconColor" in value &&
    (value.iconColor === null || typeof value.iconColor === "string");

/** A `DateOnlyRange` with each side a valid day or left out. */
export const asDateOnlyRange = <TValue>(
    value: TValue,
): DateOnlyRange | null => {
    if (!isObject(value)) {
        return null;
    }
    const from = "from" in value ? asDateOnly(value.from) : null;
    const to = "to" in value ? asDateOnly(value.to) : null;
    return {
        ...(from !== null && { from }),
        ...(to !== null && { to }),
    };
};
